import {
  Checkbox,
  Container,
  Divider,
  RadioGroup,
  CircularProgress,
  FormControlLabel,
  Radio,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import MDAutocomplete from "components/MDAutocomplete";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDDatePicker from "components/MDDatePicker";
import MDEditor from "components/MDEditor";
import MDTypography from "components/MDTypography";
import ViewTemplateForms from "./ViewTemplateForms";
import RichTextEditor from "components/common/RichTextEditor";
import { DatePicker, TimePicker, LocalizationProvider } from "@mui/x-date-pickers"; // Import MUI DatePicker and TimePicker
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"; // Import AdapterDayjs for date management

// Component to display form preview with default fields and template fields
const ViewFormPreview = ({ defaultFieldData, watchTemplate }) => {
  const [templateData, setTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Effect to fetch template data when component mounts or watchTemplate changes
  useEffect(() => {
    setTemplateData(watchTemplate);
    setIsLoading(false);
  }, [watchTemplate?.length]);

  // Function to handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
  };

  // Function to generate initial values for form fields
  const generateInitialValues = () => {
    const initialValues = {};

    // Add default field values
    defaultFieldData?.forEach((field) => {
      if (field.type === "checkbox" || field.type === "radio-group") {
        initialValues[field.name] = [];
      } else if (field.type === "select" && field.multiple) {
        initialValues[field.name] = [];
      } else {
        initialValues[field.name] = "";
      }
    });

    // Add template field values
    templateData?.forEach((template) => {
      template?.service_add_form_fields?.forEach((field) => {
        if (field.type === "checkbox" || field.type === "radio-group") {
          initialValues[field.name] = [];
        } else if (field.type === "select" && field.multiple) {
          initialValues[field.name] = [];
        } else {
          initialValues[field.name] = "";
        }
      });
    });

    return initialValues;
  };

  return (
    // Container for form preview
    <Container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      {isLoading ? (
        // Show loading spinner while fetching data
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      ) : (
        // Wrap form in LocalizationProvider for date/time pickers
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Formik
            initialValues={generateInitialValues()}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, handleChange, setFieldValue, isSubmitting }) => (
              <Form>
                <MDBox
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  }}
                  p={1}
                  rowGap={3}
                  columnGap={3}
                  width="100%"
                >
                  {/* Render default form fields */}
                  {defaultFieldData?.map((formField) => (
                    <MDBox key={formField.id}>
                      {/* Render text input field */}
                      {formField.type === "text" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <MDInput
                              {...field}
                              label={formField.label}
                              required={formField.required}
                              fullWidth
                              inputProps={{
                                minLength: formField.minlength,
                                maxLength: formField.maxlength,
                              }}
                              error={meta.touched && meta.error}
                              helperText={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render textarea field */}
                      {formField.type === "textarea" && (
                        <MDBox>
                          <MDTypography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">
                            {formField.label}
                          </MDTypography>
                          <Field name={formField.name}>
                            {({ field, meta }) => (
                              <RichTextEditor
                                {...field}
                                value={field.value}
                                onChange={(content) => setFieldValue(field.name, content)}
                                onBlur={() => setFieldValue(field.name, field.value)}
                                error={meta.touched && meta.error}
                                helperText={meta.touched && meta.error}
                              />
                            )}
                          </Field>
                        </MDBox>
                      )}
                      {/* Render number input field */}
                      {formField.type === "number" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <MDInput
                              {...field}
                              type="number"
                              fullWidth
                              label={formField.label}
                              required={formField.required}
                              error={meta.touched && meta.error}
                              helperText={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render select/autocomplete field */}
                      {formField.type === "select" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <MDAutocomplete
                              {...field}
                              fullWidth
                              label={formField.label}
                              options={formField?.DefaultField?.options ?? []}
                              multiple={formField.multiple === true}
                              required={formField.required}
                              onChange={(event, value) => setFieldValue(field.name, value)}
                              error={meta.touched && meta.error}
                              renderInput={(params) => (
                                <MDInput {...params} label={formField.label} />
                              )}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render checkbox field */}
                      {formField.type === "checkbox" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <Checkbox
                              {...field}
                              row
                              label={formField.label}
                              options={[
                                {
                                  label: "Option 1",
                                  value: "option1",
                                },
                                {
                                  label: "Option 2",
                                  value: "option2",
                                },
                              ]}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                              }}
                              error={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render radio group field */}
                      {formField.type === "radio-group" && (
                        <MDBox>
                          <MDTypography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">
                            {formField.label.includes("&amp;")
                              ? formField.label.replace("&amp;", "&")
                              : formField.label}
                          </MDTypography>
                          <RadioGroup
                            value={values[formField.name] || ""}
                            row
                            onChange={(e) => setFieldValue(formField.name, e.target.value)}
                          >
                            {(formField.values || []).map((option) => (
                              <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio />}
                                label={option.label}
                              />
                            ))}
                          </RadioGroup>
                        </MDBox>
                      )}

                      {/* Render date picker field using MUI DatePicker */}
                      {formField.type === "date" && formField.subtype === "date" && (
                        // DatePicker for date selection
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <DatePicker
                              label={formField.label}
                              value={field.value || null}
                              onChange={(value) => setFieldValue(field.name, value)}
                              slotProps={{
                                textField: {
                                  InputLabelProps: {
                                    style: {
                                      paddingRight: "1.4rem",
                                    },
                                  },
                                  fullWidth: true,
                                  required: formField.required,
                                  error: meta.touched && Boolean(meta.error),
                                  helperText: meta.touched && meta.error,
                                },
                              }}
                            />
                          )}
                        </Field>
                      )}

                      {/* Render time picker field using MUI TimePicker */}
                      {formField.type === "date" && formField.subtype === "time" && (
                        // TimePicker for time selection
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <TimePicker
                              label={formField.label}
                              value={field.value || null}
                              onChange={(value) => setFieldValue(field.name, value)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  InputLabelProps: {
                                    style: {
                                      paddingRight: "1.4rem",
                                    },
                                  },
                                  required: formField.required,
                                  error: meta.touched && Boolean(meta.error),
                                  helperText: meta.touched && meta.error,
                                },
                              }}
                            />
                          )}
                        </Field>
                      )}
                    </MDBox>
                  ))}
                </MDBox>

                {/* Add divider if template data exists */}
                {templateData.length > 0 && <Divider sx={{ mb: 2, mt: 2 }} />}
                <MDBox sx={{ mt: 2 }}>
                  {/* Render template forms */}
                  <ViewTemplateForms templateData={templateData} />
                </MDBox>
              </Form>
            )}
          </Formik>
        </LocalizationProvider>
      )}
    </Container>
  );
};

export default ViewFormPreview;
